import React, { useState } from 'react';
import { Typography, TextField, Button, Icon } from '@scale/scaleui';
import { CommunicationsHistoryTable } from './CommunicationsHistoryTable';
import isemail from 'isemail';
import { maybeCleanOutlierEmail } from '@scale/shared/email';
import { useQuery } from 'react-query';
import {
  searchCommunicationsHistoryForContributor,
  getMostRecentCommunicationsHistory,
} from 'react-scale/corp/lib/api/growth';
import { isValidObjectId } from 'react-scale/components/design-system/ObjectIdInput/ObjectIdInput';
import SAlert from 'react-s-alert';

const CommunicationsHistoryPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const {
    data: commsData,
    isLoading: isCommsDataLoading,
    error: commsDataError,
  } = useQuery(
    ['communicationsHistory', searchTerm ? maybeCleanOutlierEmail(searchTerm) : ''],
    () => {
      if (
        isValidObjectId(searchTerm) ||
        isemail.validate(maybeCleanOutlierEmail(searchTerm), { minDomainAtoms: 2 })
      ) {
        return searchCommunicationsHistoryForContributor(searchTerm);
      } else {
        return getMostRecentCommunicationsHistory();
      }
    },
    {
      enabled:
        !searchTerm ||
        isValidObjectId(searchTerm) ||
        isemail.validate(maybeCleanOutlierEmail(searchTerm), { minDomainAtoms: 2 }),
      refetchOnWindowFocus: false,
    },
  );

  if (commsDataError) {
    const errorMessage = (commsDataError as { response?: { message?: string } }).response?.message;
    SAlert.error(
      errorMessage ? `Failed to fetch history: ${errorMessage}` : 'Failed to fetch history',
      {
        timeout: 5000,
      },
    );
  }

  return (
    <div className="p-8 flex flex-col gap-4">
      <div className="flex flex-row justify-between w-full gap-5 items-center">
        <div>
          <Typography.H4>Communications History</Typography.H4>
          <Typography.Paragraph2>
            Communications sent after 8/23/24 are guaranteed to be included in the table. The 50
            most recent communications are shown if no search term is provided.
          </Typography.Paragraph2>
        </div>
      </div>
      <div className="flex items-center gap-5 w-full">
        <TextField
          className="w-[35%]"
          leadingIcon="search"
          id="search"
          value={searchTerm}
          onChange={(e: any) => {
            setSearchTerm(e.target.value);
          }}
          placeholder="Search by contributor email or ID..."
        />
        {!isCommsDataLoading && commsData?.userId && (
          <Button
            size="lg"
            variant="secondary"
            onClick={() =>
              window.open(`https://dashboard.scale.com/corp/lookup/${commsData.userId}`, '_blank')
            }
          >
            View User in Corp
            <Icon icon={['fas', 'external-link-alt']} variant="primary" />
          </Button>
        )}
      </div>
      <CommunicationsHistoryTable
        isLoading={isCommsDataLoading}
        searchTermExists={!!searchTerm}
        data={commsData}
      />
    </div>
  );
};

export default CommunicationsHistoryPage;
